import { apiClient } from "~/services/api-client";
import type { CallAudit, CallAuditPagination, CallAuditsApiResponse } from "./types";
import { ApiError, isAxiosError } from "~/shared/utils/api-error";

export type CallAuditFilterParams = {
  cities?: string[];
  whiteGlove?: string;
  locationCodes?: string[];
};

function buildCallAuditParams(
  page: number,
  limit: number,
  search?: string,
  filters?: CallAuditFilterParams
): Record<string, unknown> {
  const params: Record<string, unknown> = { page, limit };
  if (search) params.search = search;
  if (filters?.cities?.length) params.cities = filters.cities.join(',');
  if (filters?.whiteGlove) params.whiteGlove = filters.whiteGlove;
  if (filters?.locationCodes?.length) params.locationCodes = filters.locationCodes;
  return params;
}

export type FetchCallAuditsResult = {
  calls: CallAudit[];
  pagination: CallAuditPagination;
  contextualCities: string[];
};

/**
 * Fetch call audits from the API with authentication
 */
export async function fetchCallAudits(
  page: number = 1,
  limit: number = 10,
  search?: string,
  filters?: CallAuditFilterParams
): Promise<FetchCallAuditsResult> {
  try {
    const response = await apiClient.get<CallAuditsApiResponse>(
      "/call-audits",
      { params: buildCallAuditParams(page, limit, search, filters) }
    );

    if (!response.data) {
      throw new ApiError("Empty response from API", response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? "API request was not successful",
        response.status,
        response.data
      );
    }

    if (!response.data.data) {
      throw new ApiError(
        "No call audit data in response",
        response.status,
        response.data
      );
    }

    const calls = response.data.data;
    const pagination: CallAuditPagination = response.data.pagination ?? {
      total: calls.length,
      page,
      limit,
      totalPages: 1,
    };

    return { calls, pagination, contextualCities: response.data.filtersContextual?.cities ?? [] };
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message =
        responseData?.message ?? error.message ?? "Failed to fetch call audits from API";
      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      undefined,
      error
    );
  }
}
